/**
 * Which app-rail entry a URL belongs to. Kept free of React so the matching
 * rules (language prefixes, SEO aliases, the Create Song catch-all) are
 * unit-testable on their own.
 */
import {
  creativeMenuCategories,
  creativeToolIdFromSlug,
  creativeToolPresets,
  creativeTools,
} from "@shared/creativeTools";

const SUPPORTED_LANGS = new Set([
  "en",
  "ko",
  "ja",
  "zh",
  "es",
  "pt",
  "fr",
  "de",
  "it",
  "ru",
  "ar",
  "hi",
  "id",
  "vi",
  "th",
  "tr",
  "pl",
  "nl",
]);

/** The rail's entries in order, grouped. Featured leads: while it sat last
 *  (Sep 27 – Oct 6) in-app visits to /featured fell by about two thirds. */
export const RAIL_GROUPS: readonly (readonly string[])[] = [
  ["/featured"],
  [
    "/ai-music-video-generator",
    "/ai-singing-video-generator",
    "/ai-dance-video-generator",
    "/lyric-video-generator",
  ],
  ["/ai-music-generator", "/ai-cover", "/vocal-remover", "/stem-splitter"],
  ["/image-tools", "/video-tools"],
  ["/projects", "/songs", "/playlists"],
];

/** Phones show these as tabs, then a More tab whose sheet lists every rail
 *  entry. All 14 as tabs needed 632px, so a 375px phone hid the last six. */
export const PHONE_TABS: readonly string[] = [
  "/featured",
  "/ai-music-video-generator",
  "/ai-music-generator",
  "/projects",
  "/songs",
];

function stripLang(path: string): string {
  const parts = path.split("/").filter(Boolean);
  if (parts[0] && SUPPORTED_LANGS.has(parts[0])) {
    return "/" + parts.slice(1).join("/");
  }
  return path || "/";
}

// Every URL (SEO aliases included) that serves each product page.
const MUSIC_VIDEO_PATHS = new Set([
  "/ai-music-video-generator",
  "/free-ai-music-video-generator",
  "/suno-to-video",
  "/ai-music-video-director",
  "/ai-music-video-workflow",
  "/how-to-make-ai-music-video",
]);
const SINGING_PATHS = new Set([
  "/ai-singing-video-generator",
  "/singing-video-generator",
  "/tools/ai-singing-video-generator",
]);
const LYRIC_VIDEO_PATHS = new Set([
  "/lyric-video-generator",
  "/lyrics-video-generator",
  "/tools/lyric-video-generator",
  "/tools/lyrics-video-generator",
]);
const DANCE_PATHS = new Set([
  "/ai-dance-video-generator",
  "/dance-video-generator",
  "/ai-dance-generator",
  "/tools/ai-dance-video-generator",
]);

/** Which rail entry ("Image tools" / "Video tools") a creative-tools page
 *  belongs to — the header's split: audio tools sit under Video. */
function creativeMenuForPath(p: string): "image" | "video" | null {
  if (p === "/image-tools") return "image";
  if (p === "/video-tools") return "video";
  const segment = p.match(/^\/creative-tools\/([^/]+)/)?.[1];
  if (!segment) return null;
  const toolId =
    creativeToolPresets[segment]?.toolId ?? creativeToolIdFromSlug(segment);
  const tool = creativeTools.find((t) => t.id === toolId);
  if (!tool) return null;
  return creativeMenuCategories.image.includes(tool.category)
    ? "image"
    : "video";
}

export function isItemActive(path: string, href: string): boolean {
  const p = stripLang(path.split(/[?#]/)[0]);
  if (href === "/songs") {
    return p === "/songs" || p.startsWith("/song/");
  }
  if (href === "/playlists") {
    return p === "/playlists" || p.startsWith("/playlist/");
  }
  if (href === "/ai-music-video-generator") {
    return (
      MUSIC_VIDEO_PATHS.has(p) ||
      p.startsWith("/ai-music-video-generator/") ||
      p.startsWith("/ai-music-video/")
    );
  }
  if (href === "/ai-dance-video-generator") {
    return DANCE_PATHS.has(p);
  }
  if (href === "/image-tools" || href === "/video-tools") {
    return creativeMenuForPath(p) === (href === "/image-tools" ? "image" : "video");
  }
  if (href === "/ai-singing-video-generator") {
    return SINGING_PATHS.has(p);
  }
  if (href === "/lyric-video-generator") {
    return LYRIC_VIDEO_PATHS.has(p);
  }
  if (href === "/ai-music-generator") {
    if (
      p === "/ai-music-video-generator" ||
      p.startsWith("/ai-music-video-generator/") ||
      p.startsWith("/ai-music-video/") ||
      // singing/lyric/dance video tools and the image/video tools also end in
      // "-generator" but have their own nav items — don't let the Create
      // Song catch-all claim them
      p.endsWith("-video-generator") ||
      DANCE_PATHS.has(p) ||
      creativeMenuForPath(p) !== null
    ) {
      return false;
    }
    return (
      p === "/ai-music-generator" ||
      p === "/vibes" ||
      p.endsWith("-song-generator") ||
      (p.endsWith("-music-generator") && p !== "/ai-music-video-generator") ||
      p.endsWith("-generator")
    );
  }
  if (href === "/ai-cover") {
    return (
      p === "/ai-cover" ||
      p.startsWith("/ai-cover/") ||
      p === "/ai-music-cover" ||
      p === "/custom-voice"
    );
  }
  return p === href;
}

export function isMusicVideoProjectPage(path: string): boolean {
  const p = stripLang(path.split(/[?#]/)[0]);
  const parts = p.split("/").filter(Boolean);
  return (
    (parts[0] === "ai-music-video" ||
      (parts[0] === "ai-music-video-generator" && parts.length > 1)) &&
    Boolean(parts[1])
  );
}

/** Pages where the rail starts as icons whatever the user last chose: the
 *  workspace, and the music video, dance, photo sing and lyric video
 *  generators, whose composers want the width. */
export function navStartsCollapsed(path: string): boolean {
  const p = stripLang(path.split(/[?#]/)[0]);
  return (
    isMusicVideoProjectPage(path) ||
    MUSIC_VIDEO_PATHS.has(p) ||
    DANCE_PATHS.has(p) ||
    SINGING_PATHS.has(p) ||
    LYRIC_VIDEO_PATHS.has(p)
  );
}
